import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Calculator, 
  ArrowLeft,
  Play,
  Trophy,
  Coins,
  RotateCcw,
  Video,
  Sparkles,
  Check,
  X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { UserModelData, PAGE_NAME } from "../../../types";
import useThemeHook from "../../hooks/useThemeHook";
import CommonPageTitle from "../../components/CommonPageTitle";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { actions } from "../../redux";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";

type GameState = "idle" | "playing" | "finished";
type Operator = "+" | "-" | "×";

interface MathProblem {
  a: number;
  b: number;
  operator: Operator;
  answer: number;
  options: number[];
}

const NumberCrunchGame: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const [gameState, setGameState] = useState<GameState>("idle");
  const [timeLeft, setTimeLeft] = useState(30);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [problem, setProblem] = useState<MathProblem | null>(null);
  const [feedback, setFeedback] = useState<"correct" | "wrong" | null>(null);
  const [earnedATH, setEarnedATH] = useState(0);
  const [isClaimingReward, setIsClaimingReward] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [highScore, setHighScore] = useState(0);
  const [bonusMultiplier, setBonusMultiplier] = useState(1);
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [adWatched, setAdWatched] = useState(false);
  const [difficulty, setDifficulty] = useState(1);
  const [showAdGate, setShowAdGate] = useState(false);
  const [adGateLoading, setAdGateLoading] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const saved = localStorage.getItem("numberCrunchHighScore");
    if (saved) setHighScore(parseInt(saved));
  }, []);

  // Check daily play count on mount
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const key = `game_plays_number_crunch_${user?.id}_${today}`;
    const plays = parseInt(localStorage.getItem(key) || '0', 10);
    if (plays >= 1) {
      setShowAdGate(true);
    }
  }, [user?.id]);

  useEffect(() => {
    if (gameState === "playing" && timeLeft > 0) {
      timerRef.current = setTimeout(() => {
        setTimeLeft(t => t - 1);
      }, 1000);
    } else if (gameState === "playing" && timeLeft === 0) {
      endGame();
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [gameState, timeLeft]);

  const generateProblem = useCallback((): MathProblem => {
    const operators: Operator[] = ["+", "-", "×"];
    const op = operators[Math.floor(Math.random() * operators.length)];
    
    let a: number, b: number, answer: number;
    const maxNum = Math.min(10 + difficulty * 5, 50);
    
    switch (op) {
      case "+":
        a = Math.floor(Math.random() * maxNum) + 1;
        b = Math.floor(Math.random() * maxNum) + 1;
        answer = a + b;
        break;
      case "-":
        a = Math.floor(Math.random() * maxNum) + 5;
        b = Math.floor(Math.random() * Math.min(a, maxNum)) + 1;
        answer = a - b;
        break;
      case "×":
        a = Math.floor(Math.random() * Math.min(12, maxNum / 2)) + 1;
        b = Math.floor(Math.random() * 12) + 1;
        answer = a * b;
        break;
      default:
        a = 1; b = 1; answer = 2;
    }

    // Generate wrong options close to the correct answer
    const options = new Set<number>();
    options.add(answer);
    while (options.size < 4) {
      const offset = Math.floor(Math.random() * 10) - 5;
      const wrong = answer + (offset === 0 ? 1 : offset);
      if (wrong >= 0 && wrong !== answer) options.add(wrong);
    }

    return {
      a, b, operator: op, answer,
      options: Array.from(options).sort(() => Math.random() - 0.5),
    };
  }, [difficulty]);

  const handleAdGateWatch = async () => {
    setAdGateLoading(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(user?.id || '', () => {
        setShowAdGate(false);
      });
    } catch (error) {
      console.error('Ad gate error:', error);
    } finally {
      setAdGateLoading(false);
    }
  };

  const incrementPlayCount = () => {
    const today = new Date().toISOString().split('T')[0];
    const key = `game_plays_number_crunch_${user?.id}_${today}`;
    const plays = parseInt(localStorage.getItem(key) || '0', 10);
    localStorage.setItem(key, (plays + 1).toString());
  };

  const startGame = () => {
    incrementPlayCount();
    setGameState("playing");
    setTimeLeft(30);
    setScore(0);
    setStreak(0);
    setEarnedATH(0);
    setShowReward(false);
    setBonusMultiplier(1);
    setAdWatched(false);
    setDifficulty(1);
    setProblem(generateProblem());
    setFeedback(null);
  };

  const handleAnswer = (selected: number) => {
    if (gameState !== "playing" || feedback) return;

    if (selected === problem?.answer) {
      setFeedback("correct");
      setScore(s => s + 1);
      setStreak(s => s + 1);
      // Increase difficulty every 5 correct answers
      if ((score + 1) % 5 === 0) setDifficulty(d => d + 1);
      // Bonus time for streaks
      if (streak > 0 && streak % 3 === 0) setTimeLeft(t => Math.min(t + 2, 30));
    } else {
      setFeedback("wrong");
      setStreak(0);
    }

    setTimeout(() => {
      setFeedback(null);
      setProblem(generateProblem());
    }, 400);
  };

  const endGame = () => {
    setGameState("finished");

    // Calculate ATH reward
    let reward = 0.1;
    if (score >= 20) reward = 1.5;
    else if (score >= 15) reward = 1;
    else if (score >= 10) reward = 0.6;
    else if (score >= 5) reward = 0.3;

    setEarnedATH(reward);

    if (score > highScore) {
      setHighScore(score);
      localStorage.setItem("numberCrunchHighScore", score.toString());
    }

    // Interstitial ad converted to Rewarded Ad
    setTimeout(() => {
      if (user?.id) piNetworkAdsManager.handleRewardedAdFlowFront(user.id);
    }, 2000);
  };

  const watchAdForBonus = async () => {
    if (!user?.id || isWatchingAd) return;
    setIsWatchingAd(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(user.id);
      setBonusMultiplier(2);
      setAdWatched(true);
      await arthereumProvider.recordGameAdBonus(user.id, "number_crunch", earnedATH);
    } catch (error) {
      console.error("Ad failed:", error);
    } finally {
      setIsWatchingAd(false);
    }
  };

  const claimReward = async () => {
    if (!user?.id || isClaimingReward) return;
    const finalReward = parseFloat((earnedATH * bonusMultiplier).toFixed(2));
    setIsClaimingReward(true);
    try {
      await arthereumProvider.awardGameATH(user.id, "number_crunch", finalReward, score);
      dispatch(actions.setActualUser({
        ...userData,
        user: { ...user, arthereumBalance: (user.arthereumBalance || 0) + finalReward },
        miningInfos: userData.miningInfos,
      }));
      setShowReward(true);
    } catch (error: any) {
      alert(error.message || "Failed to claim reward");
    } finally {
      setIsClaimingReward(false);
    }
  };

  const playAgain = () => {
    setGameState("idle");
    setScore(0);
    setStreak(0);
    setTimeLeft(30);
    setEarnedATH(0);
    setShowReward(false);
    setBonusMultiplier(1);
    setAdWatched(false);
  };

  const finalReward = earnedATH * bonusMultiplier;

  return (
    <div className={`min-h-screen ${theme === "dark" ? "bg-slate-900" : "bg-gray-50"}`}>
      {showAdGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-6">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center space-y-4 shadow-2xl">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <Video size={32} className="text-amber-500" />
            </div>
            <h3 className="text-xl font-black text-gray-900">Watch Ad to Play Again!</h3>
            <p className="text-sm text-gray-500">Your free daily play has been used. Watch a short ad to unlock another round!</p>
            <button
              onClick={handleAdGateWatch}
              disabled={adGateLoading}
              className="w-full py-3 rounded-2xl font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-lg active:scale-95 transition-all"
            >
              {adGateLoading ? 'Loading Ad...' : '🎬 Watch Ad & Play!'}
            </button>
          </div>
        </div>
      )}
      <CommonPageTitle title="Number Crunch" padding={null} />

      <div className="max-w-lg mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(PAGE_NAME.GAMES)}
              className={`p-2 rounded-xl ${theme === "dark" ? "bg-slate-800" : "bg-white"}`}
            >
              <ArrowLeft size={20} className={theme === "dark" ? "text-white" : "text-gray-900"} />
            </button>
            <div>
              <h1 className={`text-2xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                Number Crunch 🔢
              </h1>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full ${theme === "dark" ? "bg-blue-900/30 text-blue-400" : "bg-blue-100 text-blue-600"} text-sm font-bold`}>
            🏆 Best: {highScore}
          </div>
        </div>

        {/* Game Container */}
        <div className={`
          rounded-3xl p-6 text-center
          ${theme === "dark" 
            ? "bg-gradient-to-br from-blue-900/30 to-cyan-900/30 border border-blue-500/30" 
            : "bg-gradient-to-br from-blue-100 to-cyan-100 border border-blue-300"}
        `}>
          {/* Timer & Score */}
          <div className="flex justify-around mb-6">
            <div>
              <p className={`text-xs ${theme === "dark" ? "text-slate-400" : "text-gray-500"} uppercase font-bold`}>Time</p>
              <p className={`text-4xl font-black ${gameState === "playing" && timeLeft <= 5 ? "text-red-500 animate-pulse" : theme === "dark" ? "text-white" : "text-gray-900"}`}>
                {timeLeft}s
              </p>
            </div>
            <div>
              <p className={`text-xs ${theme === "dark" ? "text-slate-400" : "text-gray-500"} uppercase font-bold`}>Score</p>
              <p className={`text-4xl font-black ${theme === "dark" ? "text-blue-400" : "text-blue-600"}`}>
                {score}
              </p>
            </div>
            {streak > 1 && (
              <div>
                <p className={`text-xs ${theme === "dark" ? "text-slate-400" : "text-gray-500"} uppercase font-bold`}>Streak</p>
                <p className="text-4xl font-black text-amber-400">
                  🔥{streak}
                </p>
              </div>
            )}
          </div>

          <AnimatePresence mode="wait">
            {gameState === "idle" && (
              <motion.div
                key="idle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <div className={`p-6 rounded-2xl ${theme === "dark" ? "bg-slate-800/50" : "bg-white/50"}`}>
                  <Calculator size={64} className="mx-auto text-blue-400 mb-4" />
                  <h2 className={`text-xl font-bold mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    How to Play
                  </h2>
                  <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
                    Solve math problems as fast as possible!
                    <br />30 seconds. Get streaks for bonus time. ⚡
                  </p>
                </div>

                <div className="space-y-2">
                  <p className={`text-xs ${theme === "dark" ? "text-slate-500" : "text-gray-400"}`}>Rewards:</p>
                  <div className="flex flex-wrap justify-center gap-2 text-xs">
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-slate-700 text-slate-300" : "bg-gray-200 text-gray-600"}`}>0-4: 0.1 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-slate-700 text-slate-300" : "bg-gray-200 text-gray-600"}`}>5-9: 0.3 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-blue-900/50 text-blue-400" : "bg-blue-100 text-blue-600"}`}>10-14: 0.6 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-blue-900/50 text-blue-400" : "bg-blue-100 text-blue-600"}`}>15-19: 1 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-emerald-900/50 text-emerald-400" : "bg-emerald-100 text-emerald-600"}`}>20+: 1.5 ATH!</span>
                  </div>
                </div>

                <button
                  onClick={startGame}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-blue-500/30 transition-all active:scale-95"
                >
                  <Play size={24} /> Start Game
                </button>
              </motion.div>
            )}

            {gameState === "playing" && problem && (
              <motion.div
                key="playing"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-6"
              >
                {/* Problem Display */}
                <motion.div
                  key={`${problem.a}-${problem.operator}-${problem.b}`}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className={`p-8 rounded-2xl ${
                    feedback === "correct" ? "bg-emerald-500/20 border-2 border-emerald-500" :
                    feedback === "wrong" ? "bg-red-500/20 border-2 border-red-500" :
                    theme === "dark" ? "bg-slate-800/50" : "bg-white/70"
                  }`}
                >
                  <p className={`text-5xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    {problem.a} {problem.operator} {problem.b} = ?
                  </p>
                  {feedback && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="mt-2 flex justify-center"
                    >
                      {feedback === "correct" ? (
                        <Check size={32} className="text-emerald-400" />
                      ) : (
                        <X size={32} className="text-red-400" />
                      )}
                    </motion.div>
                  )}
                </motion.div>

                {/* Answer Options */}
                <div className="grid grid-cols-2 gap-3">
                  {problem.options.map((option, i) => (
                    <motion.button
                      key={`${option}-${i}`}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleAnswer(option)}
                      className={`
                        py-4 rounded-2xl text-2xl font-black
                        transition-all active:scale-95
                        ${theme === "dark" 
                          ? "bg-slate-700 hover:bg-slate-600 text-white" 
                          : "bg-white hover:bg-gray-100 text-gray-900 shadow-sm"}
                      `}
                    >
                      {option}
                    </motion.button>
                  ))}
                </div>
              </motion.div>
            )}

            {gameState === "finished" && (
              <motion.div
                key="finished"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                <div className={`p-6 rounded-2xl ${theme === "dark" ? "bg-slate-800/50" : "bg-white/50"}`}>
                  <Trophy size={64} className="mx-auto text-blue-400 mb-4" />
                  <h2 className={`text-2xl font-black mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    Time's Up!
                  </h2>
                  <p className={`text-4xl font-black ${theme === "dark" ? "text-blue-400" : "text-blue-600"}`}>
                    {score} Correct
                  </p>
                  {score > highScore && score > 0 && (
                    <p className="text-emerald-400 text-sm font-bold mt-2">🎉 New High Score!</p>
                  )}
                </div>

                {/* Reward Display */}
                <div className={`p-4 rounded-2xl ${theme === "dark" ? "bg-emerald-900/30 border border-emerald-500/30" : "bg-emerald-100 border border-emerald-300"}`}>
                  <p className={`text-sm ${theme === "dark" ? "text-emerald-400" : "text-emerald-600"} mb-1`}>You earned:</p>
                  <p className={`text-3xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"} flex items-center justify-center gap-2`}>
                    <Coins className="text-yellow-400" /> 
                    {finalReward.toFixed(2)} ATH
                    {bonusMultiplier > 1 && (
                      <span className="text-emerald-400 text-sm ml-1">({bonusMultiplier}x)</span>
                    )}
                  </p>
                </div>

                {/* Watch Ad for Bonus */}
                {!showReward && !adWatched && (
                  <motion.button
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    onClick={watchAdForBonus}
                    disabled={isWatchingAd}
                    className="w-full py-3 rounded-2xl flex items-center justify-center gap-2 font-bold bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-purple-500/30 transition-all active:scale-95 disabled:opacity-50"
                  >
                    <Video size={20} />
                    {isWatchingAd ? "Loading Ad..." : (
                      <>Watch Ad for <span className="text-emerald-300">2X BONUS</span> <Sparkles size={16} className="text-yellow-300" /></>
                    )}
                  </motion.button>
                )}

                {adWatched && !showReward && (
                  <div className="p-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-center text-sm font-bold">
                    ✨ 2X BONUS APPLIED!
                  </div>
                )}

                {!showReward ? (
                  <button
                    onClick={claimReward}
                    disabled={isClaimingReward}
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-emerald-500/30 transition-all active:scale-95 disabled:opacity-50"
                  >
                    {isClaimingReward ? "Claiming..." : `Claim ${finalReward.toFixed(2)} ATH 🎁`}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="p-3 rounded-xl bg-emerald-500 text-white text-center font-bold">
                      ✅ Reward Claimed!
                    </div>
                    <button
                      onClick={playAgain}
                      className="w-full py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg transition-all active:scale-95"
                    >
                      <RotateCcw size={20} /> Play Again
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default NumberCrunchGame;
